"use client"

import * as React from "react"
import { Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useTranslation } from "@/lib/i18n"
import { richText } from "@/lib/i18n/rich-text"

export interface TypedConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** User must type this exactly (per matchMode) to enable confirm, e.g. "STOP", "CANCEL", "DELETE" */
  phrase: string
  /** Dialog title, e.g. "Stop Campaign?" */
  title: string
  /** Dialog body — pass JSX so callers can include resource names with <strong> */
  description: React.ReactNode
  /** Confirm button label. Defaults to `Confirm ${phrase}` e.g. "Confirm STOP" */
  confirmLabel?: string
  /** Cancel button label. Defaults to "Cancel" */
  cancelLabel?: string
  /** Controls confirm button color. Defaults to 'destructive' */
  variant?: "destructive" | "warning"
  /** Called when user confirms (button enabled and clicked) */
  onConfirm: () => void | Promise<void>
  /** Shows spinner + disables buttons while async confirm runs */
  isPending?: boolean
  /** Whether phrase matching is case-sensitive. Defaults to 'case-sensitive' */
  matchMode?: "case-sensitive" | "case-insensitive"
  /**
   * Applied to what was typed before it is compared with the phrase, e.g. folding
   * Arabic-Indic or Devanagari digits and dropping grouping separators when the phrase
   * is a count ("١٬٢٣٤" confirms 1234). The field still shows what the person typed.
   */
  normalizeInput?: (value: string) => string
}

export function TypedConfirmDialog({
  open,
  onOpenChange,
  phrase,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  variant = "destructive",
  onConfirm,
  isPending = false,
  matchMode = "case-sensitive",
  normalizeInput,
}: TypedConfirmDialogProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = React.useState("")
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Reset input whenever the dialog opens
  React.useEffect(() => {
    if (open) {
      setInputValue("")
      // Auto-focus on next tick so DialogContent animation doesn't swallow focus
      const id = setTimeout(() => {
        inputRef.current?.focus()
      }, 50)
      return () => clearTimeout(id)
    }
  }, [open])

  const normalise = (v: string) =>
    matchMode === "case-insensitive" ? v.toLowerCase() : v

  const typed = normalizeInput ? normalizeInput(inputValue) : inputValue
  const isMatch = normalise(typed) === normalise(phrase)
  // Say why in words (staging #756: a red border alone told the operator
  // nothing). Not while what is typed is still the start of the phrase: "ST"
  // is not wrong yet, "stop" or "STOPP" is.
  const diverges =
    inputValue.length > 0 &&
    !isMatch &&
    !normalise(phrase).startsWith(normalise(typed))

  const resolvedConfirmLabel = confirmLabel ?? `Confirm ${phrase}`

  const handleConfirm = async () => {
    if (!isMatch || isPending) return
    await onConfirm()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isMatch && !isPending) {
      void handleConfirm()
    }
  }

  const handleCancel = () => {
    if (isPending) return
    onOpenChange(false)
  }

  const confirmButtonClass =
    variant === "warning"
      ? "bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-600/20 dark:focus-visible:ring-amber-600/40"
      : undefined // 'destructive' uses the Button variant prop directly

  return (
    <Dialog open={open} onOpenChange={isPending ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="typed-confirm-input">
              {richText(t("common.typedConfirm.label"), {
                phrase: (
                  <span dir="ltr" className="font-mono text-primary font-semibold">
                    {phrase}
                  </span>
                ),
              })}
            </Label>
            <Input
              ref={inputRef}
              id="typed-confirm-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              // The phrase as it must be typed. It was lowercased, which for a
              // case-sensitive gate prompted the operator with the one string
              // that would be refused (staging #756).
              placeholder={phrase}
              disabled={isPending}
              aria-label={t("common.typedConfirm.label", { phrase })}
              aria-invalid={diverges || undefined}
              aria-describedby={diverges ? "typed-confirm-hint" : undefined}
              className={cn(
                inputValue.length > 0 &&
                  !isMatch &&
                  "border-destructive focus-visible:ring-destructive focus-visible:ring-[3px]",
              )}
            />
            {diverges && (
              <p id="typed-confirm-hint" role="alert" className="text-xs text-destructive">
                {t(
                  matchMode === "case-insensitive"
                    ? "common.typedConfirm.mismatch"
                    : "common.typedConfirm.mismatchCaseSensitive",
                  { phrase },
                )}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === "destructive" ? "destructive" : "default"}
            onClick={() => void handleConfirm()}
            disabled={!isMatch || isPending}
            aria-disabled={!isMatch || isPending}
            className={confirmButtonClass}
          >
            {isPending && <Loader2 className="size-4 animate-spin" />}
            {resolvedConfirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
